import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Layers, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/shared/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import Breadcrumb from '@/shared/components/Breadcrumb';
import BulkEditDialog from '@/shared/components/BulkEditDialog';
import BulkResultDialog from '@/shared/components/BulkResultDialog';
import PilotageResultDialog from '@/shared/components/PilotageResultDialog';
import useBulkAction from '@/shared/hooks/useBulkAction';
import useBulkSelection from '@/shared/hooks/useBulkSelection';
import useCommandConfirmation from '@/shared/hooks/useCommandConfirmation';
import useRafraichissementTempsReel from '@/shared/hooks/useRafraichissementTempsReel';
import useSuiviOrdre from '@/shared/hooks/useSuiviOrdre';
import IndicateurFlux from '@/shared/components/IndicateurFlux';
import { bulkAssetAction } from '@/shared/api/bulk';
import {
  executeAssetAction, fetchPilotageActions, fetchPilotageAssets, fetchPilotageJournal,
} from '@/shared/api/pilotage';
import AssetPilotageRow from '@/features/pilotage/components/AssetPilotageRow';
import { ConfirmSingleDialog } from '@/features/pilotage/components/PilotageConfirm';
import PilotageJournal from '@/features/pilotage/components/PilotageJournal';


const TABS = [
  ['all', 'Tous'], ['PAC', 'PAC'], ['Ballon', 'Ballon ECS'],
  ['Batterie', 'Batterie'], ['PV', 'PV'],
];

const HETEROGENE_HINT = types => (
  <>
    Votre sélection mélange {types.length} types d'actifs ({types.join(', ')}).
    Les actions diffèrent d'un type à l'autre — une batterie ne se pilote pas comme un
    panneau photovoltaïque. <strong>Filtrez par type</strong> avec les onglets, puis
    relancez le pilotage groupé.
  </>
);

/**
 * Écran de pilotage du parc — actions unitaires et groupées, par type d'actif.
 *
 * Ce que cet écran ne fait PAS : décider de ce qui est pilotable. Le catalogue vient
 * de `/pilotage/actions`, et c'est lui qui dit, action par action, si le geste part en
 * MQTT, en Tuya, ou n'est qu'une trace. L'UI se contente de le rendre visible.
 *
 * Aucune commande ne se solde par un simple toast : `PilotageResultDialog` sépare ce
 * que le backend a publié de ce que l'actif a réellement fait, et le pilotage groupé
 * ouvre son compte rendu même quand tout passe.
 */
export default function PilotagePage() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [connFilter, setConnFilter] = useState('');
  // Le pendant du filtre du Monitoring, meme parametre serveur, meme vocabulaire : un
  // exploitant qui apprend le filtre sur un ecran le retrouve sur l'autre.
  const [mandatFilter, setMandatFilter] = useState('');

  const [pending, setPending] = useState(null);          // action unitaire à confirmer
  const [result, setResult] = useState(null);            // compte rendu de l'action unitaire
  const [bulkOpen, setBulkOpen] = useState(false);
  const [journal, setJournal] = useState([]);
  const [journalOpen, setJournalOpen] = useState(false);

  // La boucle de vérification relit l'actif toutes les 2 s pendant une minute : on fait
  // redescendre cette télémétrie dans la ligne, au lieu de la laisser mourir dans la
  // popup. Zéro appel réseau supplémentaire, et la ligne bouge en même temps qu'elle.
  const applyTelemetry = useCallback((assetId, fresh) => {
    setAssets(prev => prev.map(a => (a.id === assetId ? {
      ...a,
      statut_connexion: fresh.statut_connexion ?? a.statut_connexion,
      dernier_contact: fresh.dernier_contact ?? a.dernier_contact,
      monitoring: { ...a.monitoring, ...fresh },
    } : a)));
  }, []);

  const confirmation = useCommandConfirmation(applyTelemetry);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (tab !== 'all') params.type = tab;
      if (search) params.search = search;
      if (connFilter) params.statut_connexion = connFilter;
      if (mandatFilter) params.statut_mandat = mandatFilter;
      const [list, actions] = await Promise.all([
        fetchPilotageAssets(params), fetchPilotageActions(),
      ]);
      setAssets(list);
      setMatrix(actions);
    } catch {
      toast.error('Impossible de charger le pilotage');
    } finally {
      setLoading(false);
    }
  }, [tab, search, connFilter, mandatFilter]);

  /**
   * Battement de fond : rafraîchit la liste, RIEN d'autre.
   *
   * Ne redemande pas le catalogue d'actions (il ne bouge pas) et n'alerte pas en cas
   * d'échec — un toast d'erreur toutes les dix secondes sur une coupure réseau serait
   * une punition, pas une information.
   */
  const refreshAssets = useCallback(async () => {
    try {
      const params = {};
      if (tab !== 'all') params.type = tab;
      if (search) params.search = search;
      if (connFilter) params.statut_connexion = connFilter;
      if (mandatFilter) params.statut_mandat = mandatFilter;
      setAssets(await fetchPilotageAssets(params));
    } catch { /* silencieux, on retente au battement suivant */ }
  }, [tab, search, connFilter, mandatFilter]);

  useEffect(() => { load(); }, [load]);

  // Un parc bouge sans qu'on lui ait rien demandé : un agrégateur active, le client
  // consomme. Sans rafraîchissement, la ligne restait figée sur l'état du chargement de
  // la page. Depuis le 30/09 (retour de David), le flux temps réel dit quand relire ; à
  // défaut, on relit toutes les 10 s. La boucle de vérification batterie prend toujours
  // le relais à 2 s pendant une commande.
  const idsAffiches = useMemo(() => new Set(assets.map(a => a.id)), [assets]);
  // Les trames batterie seules ne relisent la liste que toutes les 10 s, le rythme
  // d'avant : elle n'est pas paginée (0,7 s, 280 Ko sur le staging). Un ordre ou un
  // relevé, lui, la relit en 2 s.
  const { etat: etatFlux } = useRafraichissementTempsReel({
    relire: refreshAssets, ids: idsAffiches, ecartTramesMs: 10000,
  });
  const suiviOrdre = useSuiviOrdre();

  // Un actif verrouillé par un agrégateur, ou dont le mandat n'est pas signé, n'entre
  // pas dans la sélection : « tout sélectionner » ne coche donc que ce sur quoi on a
  // réellement la main.
  //
  // ⚠️ LE MANDAT N'EN FAIT PLUS PARTIE — arbitrage du 31/08. Un actif sans mandat est
  // désormais sélectionnable et pilotable ; seul le verrou agrégateur l'exclut, parce
  // que celui-là est un conflit de commande réel, pas une question de droit.
  const selectable = useMemo(
    () => assets.filter(a => !a.aggregateur_lock), [assets]);
  // Toujours compté, toujours annoncé : le pilotage s'ouvre, il ne se tait pas. C'est
  // exactement ce que Boubacar demande — « on peut garder la couleur et l'indication
  // qu'il n'y a pas de mandat, mais on doit quand même pouvoir piloter ».
  const sansMandat = useMemo(
    () => assets.filter(a => a.sans_mandat === true).length, [assets]);
  const sel = useBulkSelection(selectable);
  const bulk = useBulkAction(() => { load(); sel.clear(); });

  const assetLabel = useCallback((id) => {
    const a = assets.find(x => x.id === id);
    return a ? `${a.client_nom} — ${a.type} ${a.marque || ''}`.trim() : id;
  }, [assets]);

  // Actions proposables à un lot : le catalogue complet si la sélection est homogène,
  // sinon rien — proposer une action « commune » à des types différents reviendrait à
  // promettre un geste qui n'existe pas pour la moitié des cibles. Le dialogue affiche
  // alors comment s'en sortir plutôt qu'une liste vide.
  const bulkTypes = useMemo(
    () => [...new Set(sel.selectedItems.map(a => a.type))], [sel.selectedItems]);
  const bulkActions = useMemo(
    () => (bulkTypes.length === 1 ? (matrix[bulkTypes[0]] || {}) : {}), [bulkTypes, matrix]);

  const openJournal = async () => {
    setJournalOpen(true);
    try { setJournal(await fetchPilotageJournal(100)); }
    catch { toast.error('Erreur au chargement du journal'); }
  };

  const askAction = (asset, action, value) => setPending({ asset, action, value });

  const closeResult = () => { confirmation.cancel(); suiviOrdre.arreter(); setResult(null); };

  const runSingle = async () => {
    const { asset, action, value } = pending;
    const def = matrix[asset.type]?.[action];
    setPending(null);
    try {
      const res = await executeAssetAction(asset.id, action, value ?? null);
      setResult({
        ...res,
        asset,
        batteryAction: def?.battery_action,
        actionLabel: def?.label || action,
        value: value ?? def?.default_value ?? null,
        valueUnit: def?.value_unit,
        at: new Date().toLocaleString('fr-FR'),
      });
      // Un ordre S-Miles part « envoyé, pas confirmé » : la popup suit sa trace jusqu'à
      // l'état final, au lieu de renvoyer l'utilisateur au journal.
      if (res.success && res.confirme === false) suiviOrdre.suivre(res.operation);
      // La relecture d'état n'existe que pour les batteries : ailleurs, aucun champ de
      // télémétrie ne reflète le geste, et un faux « confirmé » vaudrait moins que rien.
      if (res.success && !res.simulated) {
        confirmation.verify(
          asset.id, def?.battery_action,
          asset.type === 'Batterie' && def?.integration === 'mqtt');
      } else {
        // Pas de relecture pour cette action : un rafraîchissement immédiat est le seul
        // moyen de refléter ce qui a pu changer côté données.
        confirmation.reset();
        refreshAssets();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setResult({
        success: false,
        error: typeof detail === 'string' ? detail : "Erreur lors de l'exécution",
        asset,
        actionLabel: def?.label || action,
        value: value ?? null,
        valueUnit: def?.value_unit,
        at: new Date().toLocaleString('fr-FR'),
      });
    }
  };

  const runBulk = async (code, value) => {
    setBulkOpen(false);
    await bulk.run(() => bulkAssetAction(sel.ids, code, value), { alwaysReport: true });
  };

  const pendingDef = pending ? matrix[pending.asset.type]?.[pending.action] : null;

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="pilotage-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Breadcrumb items={[{ label: 'Pilotage des actifs' }]} />
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
              Pilotage des actifs
            </h1>
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">
              Admin uniquement
            </Badge>
          </div>
          <p className="text-sm text-neutral-400 mt-0.5">
            Toutes les actions sont tracées avec horodatage et identité utilisateur ·{' '}
            {assets.length} actif{assets.length > 1 ? 's' : ''} ·{' '}
            {selectable.length} pilotable{selectable.length > 1 ? 's' : ''}
            {sansMandat > 0 && (
              <span data-testid="pilotage-compte-sans-mandat">
                {' '}· {sansMandat} sans mandat signé
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <IndicateurFlux etat={etatFlux} />
          <Button variant="outline" size="sm" onClick={openJournal} data-testid="open-journal-btn">
            <Clock className="w-3.5 h-3.5 mr-1" /> Journal des actions
          </Button>
          {sel.count > 0 && (
            <Button size="sm" onClick={() => setBulkOpen(true)} data-testid="open-bulk-btn">
              <Layers className="w-3.5 h-3.5 mr-1" /> Pilotage groupé ({sel.count})
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {TABS.map(([value, label]) => (
              <TabsTrigger key={value} value={value} data-testid={`tab-${value.toLowerCase()}`}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
          <Input
            className="h-8 w-56 pl-7 text-[12px]"
            placeholder="Rechercher client, marque…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="pilotage-search"
          />
        </div>

        <Select value={connFilter || 'all'} onValueChange={v => setConnFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-[150px] text-[12px]" data-testid="pilotage-conn-filter">
            <SelectValue placeholder="Connexion" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes connexions</SelectItem>
            <SelectItem value="connecte">Connectés</SelectItem>
            <SelectItem value="deconnecte">Déconnectés</SelectItem>
            <SelectItem value="inconnu">Inconnu</SelectItem>
          </SelectContent>
        </Select>

        <Select value={mandatFilter || 'all'} onValueChange={v => setMandatFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-[160px] text-[12px]" data-testid="pilotage-mandat-filter">
            <SelectValue placeholder="Mandat" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous mandats</SelectItem>
            <SelectItem value="non_signe">En attente de mandat</SelectItem>
            <SelectItem value="actif">Sous mandat</SelectItem>
          </SelectContent>
        </Select>

        <span className="ml-auto text-[11px] text-neutral-500 font-medium" data-testid="pilotage-count">
          {selectable.length} sélectionnable{selectable.length > 1 ? 's' : ''}
          {sel.count > 0 && ` · ${sel.count} choisi${sel.count > 1 ? 's' : ''}`}
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-400 py-8 text-center">Chargement…</p>
      ) : assets.length === 0 ? (
        <p className="text-sm text-neutral-400 py-8 text-center">Aucun actif à afficher</p>
      ) : (
        <div className="space-y-2" data-testid="pilotage-list">
          <div className="flex items-center gap-3 px-3 py-2 text-[11px] uppercase tracking-wider text-neutral-400 font-medium">
            <Checkbox
              checked={sel.allSelected}
              onCheckedChange={sel.toggleAll}
              disabled={selectable.length === 0}
              data-testid="select-all-checkbox"
            />
            <span>Tout sélectionner (hors verrouillés et sans mandat)</span>
          </div>

          {assets.map(asset => (
            <AssetPilotageRow
              key={asset.id}
              asset={asset}
              actions={matrix[asset.type] || {}}
              selected={sel.isSelected(asset.id)}
              onToggle={() => sel.toggle(asset.id)}
              onAction={(action, value) => askAction(asset, action, value)}
              onOpenClient={() => navigate(`/clients/${asset.client_id}`)}
            />
          ))}
        </div>
      )}

      <ConfirmSingleDialog
        pending={pending}
        actionDef={pendingDef}
        onCancel={() => setPending(null)}
        onConfirm={runSingle}
      />

      <PilotageResultDialog
        result={result}
        confirm={confirmation}
        suiviOrdre={suiviOrdre.suivi}
        onClose={closeResult}
      />

      <BulkEditDialog
        open={bulkOpen}
        title="Pilotage groupé"
        noun="actif"
        count={sel.count}
        actions={bulkActions}
        selectedItems={sel.selectedItems}
        blockedReason={bulkTypes.length > 1 ? HETEROGENE_HINT(bulkTypes) : null}
        busy={bulk.busy}
        onCancel={() => setBulkOpen(false)}
        onSubmit={runBulk}
      />

      <BulkResultDialog
        report={bulk.report}
        onClose={bulk.clearReport}
        title="Résultat du pilotage groupé"
        resolveLabel={assetLabel}
      />

      <PilotageJournal open={journalOpen} onOpenChange={setJournalOpen} entries={journal} />
    </div>
  );
}
